/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useState, useEffect, useContext} from "react";

import {useDispatch} from "react-redux";
import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";

import ApplicationApiContext from "@Contexts/Api";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";

import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";


import {Button, Col, Form, Row} from "react-bootstrap";

import {AiFillMail} from "react-icons/all";

import DefaultMessageComponent from "@Components/UI/Communicates/Messages/Default";
import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";


interface CONTACT_REASONS_INTERFACE {
    Order: number;
    Text: string;
}


interface CONTACT_TYPE_INTERFACE {
    Type: "M" | "P",
    Address: string,
    Reasons?: CONTACT_REASONS_INTERFACE[]
}


type ContactPageMailProps = {contactData: CONTACT_TYPE_INTERFACE};


export const ContactPageMailComponent: React.FC<ContactPageMailProps> = ({contactData = null}): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {UserInterface | null} Application Logged User (from Global Store Object) */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    const REST_API_REQUEST_STATUS= useTypedSelector(
        (state):ReturnType<any> => state.interface
    )?.apiRequestStatus;

    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const [contactFormOpenStatus, setContactFormOpenStatus] = useState<boolean>(false);


    const [contactFormErrors, setContactFormErrors] = useState<any>({});
    const [contactFormSubmitStatus, setContactFormSubmitStatus] = useState<boolean>(false);

    const [contactFormSubject, setContactFormSubject] = useState<string>("0");
    const [contactFormContent, setContactFormContent] = useState<string>("");

    const changeContactFormSubject = (subject: string) => {
        if (subject !== "0") setContactFormErrors({...contactFormErrors, formSubject: null});
        setContactFormSubject(subject)
    };

    const changeContactFormContent = (content: string) => {
        if (content.length > 9) setContactFormErrors({...contactFormErrors, formContent: null});
        setContactFormContent(content)
    };


    const resetContactForm = () => {
        setContactFormOpenStatus(false);
        setContactFormErrors({});
        setContactFormSubmitStatus(false);
        setContactFormSubject("0");
        setContactFormContent("");
    };

    useEffect(() => {
        return () => {
            resetContactForm();
        };
    }, []);


    if (!contactData) return null;


    const {Address: contactMailAddress, Reasons: contactMailReasons} = contactData;


    if (contactFormSubmitStatus) return <DefaultMessageComponent messageText={`Wiadomość została wysłana`} messageType={"success"} />;


    const CONTACT_FORM_SUBMIT_HANDLER = async () => {

        const errors: any = {};

        if (contactFormSubject === "0") errors.formSubject = "Wybierz temat";
        if (contactFormContent.length < 10) errors.formContent = "Treść wiadomośći jest zbyt krótka";

        setContactFormErrors(errors);

        if (Object.entries(errors).length) return null;

        await CONTACT_FORM_REQUEST_HANDLER();

    };


    const CONTACT_FORM_REQUEST_HANDLER = async () => {

        const CONTACT_FORM_SEND_MESSAGE_API_URL = REST_API_URL + "/App/Mail/Send";

        const CONTACT_FORM_SEND_MESSAGE_REQUEST_DATA = {
            UserId: LOGGED_USER.userID,
            Address: contactMailAddress,
            Reason: contactFormSubject,
            Text: contactFormContent
        };

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true));

        try {

            const CONTACT_FORM_RESPONSE = await REST_API_REQUEST(
                CONTACT_FORM_SEND_MESSAGE_API_URL,
                CONTACT_FORM_SEND_MESSAGE_REQUEST_DATA,
                REST_API_REQUEST_TOKEN
            );

            if (CONTACT_FORM_RESPONSE.status === 204) {
                setContactFormSubmitStatus(true);
            } else {
                const CONTENT = await CONTACT_FORM_RESPONSE.json();
                setContactFormErrors({process: CONTENT.UserMessage});
            }

        } catch (e) {
            setContactFormErrors({process: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"})
        }

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

    };


    return (
        <Col xs={12} className={"contact-page-mail-component contact-page-contact-component"}>
            <Row className={"contact-page-mail-content"}>

                <Col xs={12} className={"contact-page-contact-header"}>
                    <AiFillMail />
                    <span>{`Formularz Kontaktowy`}</span>
                </Col>

                <Col xs={12} className={"contact-page-contact-address"}>
                    <a href={`mailto:${contactMailAddress}`} title={`Napisz do nas`}>
                        <span className={"contact-address-value"}>{contactMailAddress}</span>
                        <span className={"contact-address-icon"}>
                            <AiFillMail />
                        </span>
                    </a>
                </Col>

                <Col xs={12} className={"contact-page-contact-switcher"}>
                    <Button variant={"primary"} size={"lg"} onClick={() => setContactFormOpenStatus(!contactFormOpenStatus)}>
                        <span>{contactFormOpenStatus ? `Zrezygnuj` : `Napisz do nas`}</span>
                    </Button>
                </Col>

                {contactFormOpenStatus &&
                <Col as={Form} xs={12} className={"user-form-component contact-page-form"}>
                    <Row className={"user-form-content"}>

                        {!!contactFormErrors?.process &&
                        <ErrorMessageComponent messageText={contactFormErrors?.process} />
                        }

                        <Col xs={12} className={"contact-form-subject"}>
                            <Form.Label>{`Wybierz temat`}:</Form.Label>
                            <Form.Control
                                as={"select"}
                                value={contactFormSubject}
                                onChange={e => changeContactFormSubject(e.target.value)}
                                disabled={!!REST_API_REQUEST_STATUS}
                            >
                                <option value={"0"}>{`- wybierz -`}</option>
                                {contactMailReasons?.map((reason: CONTACT_REASONS_INTERFACE) =>
                                <option key={reason.Order} value={reason.Text}>{reason.Text}</option>
                                )}
                            </Form.Control>
                            {contactFormErrors?.formSubject && <Form.Label className={"error"}>{contactFormErrors?.formSubject}</Form.Label>}
                        </Col>

                        <Col xs={12} className={"contact-form-content"}>
                            <Form.Label>{`Treść Wiadomośći`}:</Form.Label>
                            <Form.Control
                                as={"textarea"}
                                value={contactFormContent}
                                onChange={e => changeContactFormContent(e.target.value)}
                                disabled={!!REST_API_REQUEST_STATUS}
                            />
                            {contactFormErrors?.formContent && <Form.Label className={"error"}>{contactFormErrors?.formContent}</Form.Label>}
                        </Col>

                        <Col xs={12} className={"contact-form-submit"}>
                            <Button disabled={!!REST_API_REQUEST_STATUS} variant={"primary"} size={"lg"} onClick={CONTACT_FORM_SUBMIT_HANDLER}>
                                <span>{`Wyślij`}</span>
                            </Button>
                        </Col>

                    </Row>
                </Col>
                }

            </Row>
        </Col>
    );

};


export default ContactPageMailComponent;
